import { Box, IconButton, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowClassNameParams, GridValueGetterParams } from '@mui/x-data-grid';
import { t } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react'
import I18nDataGrid from '../global/I18nDataGrid';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { TTrainingSection } from '../../shared/types/training.type';
import { convertStrideFrequency, convertStrideLength, formatMetricIntoImperialDistance, getSpeedForChart } from '../../shared/utils/conversion';
import dayjs from 'dayjs';
import { ChevronRight, KeyboardArrowDown } from '@mui/icons-material';
import type { TIntervals } from '../../shared/types/training.type'
import { get, isEmpty, isNull, isUndefined, maxBy, uniqBy, uniqueId } from 'lodash';
import { useAuth } from '../../hooks/useAuth';
import { v4 as uuidv4 } from 'uuid';

import '../../eq-icons.css'
import Icon from '../global/Icon';
import toggleItemInArray from '../../shared/utils/arrays';
import { CompiledInterval, Sectional } from '../../shared/types/sectionals.type';

type intervalGridProps = {
    intervals: Sectional[],
    currentIndex: number,
    loading: boolean,
    time: number[],
    isFinishLineActive: boolean,
    focusOnMainWork: boolean,
    printable?: boolean,

}
function IntervalsGrid({ printable = false, intervals, loading, currentIndex, time, isFinishLineActive, focusOnMainWork }: intervalGridProps) {
    const unitSystem = useSelector<RootState>(state => state.user.user?.unit_system) as "metric" | "imperial" | undefined
    const imperial_distance_miles_only = useSelector<RootState>(state => state.user.user?.imperial_distance_miles_only) as boolean
    const [selectedRows, setSelectedRows] = useState<number[]>([])
    const [Intervals, setIntervals] = useState<any>([])
    const { getSpeedUnit, getMetric } = useAuth()


    const initialIntervals = useMemo(() => {
        if (isEmpty(intervals)) return []

        let best_section_index = intervals.findIndex(interval => interval?.bestSection)

        if (best_section_index !== -1) {

            let best_section = intervals[best_section_index]
            let next_best_section = intervals.find(interval => interval.afterFinishSection)
            let finish_line_section = intervals.find(interval => interval.finish)

            let arr = [best_section]


            if (next_best_section && focusOnMainWork) {

                //Check ids if they are identique
                if (best_section.idSection === next_best_section.idSection) {
                    //We take section before fl and after
                    if (finish_line_section) arr.unshift(finish_line_section)
                } else {
                    arr.push(next_best_section)
                }

            }

            let best_section_ids = arr.map(i => i.idSection)

            setSelectedRows(best_section_ids)

            return focusOnMainWork ? arr : intervals

        }

        return focusOnMainWork ? [] : intervals


    }, [intervals, focusOnMainWork])

    useEffect(() => {
        setIntervals(initialIntervals)
    }, [initialIntervals])

    useEffect(() => {


        if (selectedRows) {
            let data = [...initialIntervals]

            selectedRows.forEach(row => {
                let find = data.findIndex((e: Sectional) => e.idSection === row)

                console.log({ find: data[find] });

                if (find !== -1) {

                    let current_section = data[find]

                    let itv: any[] = current_section?.compiledIntervals ?? []

                    let intervals_length = itv.length - 1

                    itv = itv.map((i: any, index) => ({
                        ...i,
                        'idSection': uuidv4(),
                        parentId: get(data, `[${find}].idSection`, null),
                        ...(current_section.finish && index === intervals_length ? { finish: true } : {})
                    }))
                    data.splice(find + 1, 0, ...itv)

                }
            })

            setIntervals(data)
        } else {
            setIntervals(initialIntervals)
        }
    }, [selectedRows, initialIntervals])


    const handleRowClick = (id: number) => {
        //Commented to handle multi row click
        setSelectedRows(toggleItemInArray(selectedRows, id))
        // selectedRows !== id ? setSelectedRows(id) : setSelectedRows(null)

    }

    const renderFinishLineSection = (sectional: Sectional) => {
        return (isFinishLineActive && sectional?.finish && !selectedRows.includes(sectional.idSection)) ?
            <div className='finish_section' style={{ position: 'absolute', bottom: 0, left: 0, right: 0, width: '100%' }}></div> : <></>
    }

    const columns: GridColDef[] = [
        {
            field: 'gait',
            headerAlign: 'center',
            minWidth: 100,
            renderHeader: () => (
                <Box position={'relative'} width={'100%'} height={'100%'} padding={0} mb={2} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                    <Icon name='direction'></Icon>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }}>{t('map:allure')}</Typography>
                </Box>
            ), ...(printable ? {width:100}:{flex:1}),
            sortable: false,
            renderCell: (val: GridRenderCellParams<Sectional>) => {

                let hasIntervals = val.row.gait !== 0 && (val.row?.compiledIntervals ?? []).length > 0

                return (
                    <Box display={'flex'} position={'relative'} gap={0} pl={hasIntervals ? 1 : '40px'} alignItems={'center'} width={'100%'} height={'100%'}>
                        {
                            get(val, 'row.parentId', null) ? <></> : <>
                                {hasIntervals &&
                                    <IconButton size='small' >
                                        {selectedRows.includes(val.row.idSection) ? <KeyboardArrowDown></KeyboardArrowDown> : <ChevronRight></ChevronRight>}
                                    </IconButton>
                                }

                                <Typography variant='inherit' >
                                    {(isFinishLineActive && val.row.finish) && '🏁 '}
                                    {isUndefined(val.row.gait) ? "" : t("intervals:gait:" + val.row.gait)}</Typography>
                            </>
                        }

                        {
                            renderFinishLineSection(val.row)
                        }
                    </Box>)
            }
        },

        {
            field: 'distance',
            headerAlign: 'center',
            renderHeader: () => (
                <Box position={'relative'} width={'100%'} height={'100%'} padding={0} mb={2} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                    <Icon name='direction'></Icon>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }} >{t('intervals:distance')}</Typography>
                    <Typography variant='caption'>({unitSystem === 'metric' ? t('intervals:meters') : t('intervals:furlongs')})</Typography>

                </Box>
            ),
            ...(printable ? {width:100}:{flex:1}),
            minWidth: 100,

            sortable: false,
            renderCell: (val: GridRenderCellParams<Sectional | CompiledInterval>) => {


                if ('gait' in val.row && val.row.gait == 0) {
                    return null; // do not display distance when inactivity
                }


                let distance = get(val, 'row.parentId', null) ? Math.round((val.row as CompiledInterval).dist) : Math.round((val.row as Sectional).averages?.distance ?? 0)

                return (
                    <Box position={'relative'} width={'100%'} height={'100%'}>
                        <Box padding={0} display={'flex'} gap={1} alignItems={'center'} justifyContent={'center'} position={'relative'} flex={1} height={'100%'}>
                            <Typography variant='inherit'>
                                {unitSystem === 'metric' ? distance : formatMetricIntoImperialDistance(distance, imperial_distance_miles_only)}
                            </Typography>
                        </Box>
                        {
                            renderFinishLineSection(val.row as any)
                        }
                    </Box>
                )
            }

        },
        {
            field: 'time',
            headerAlign: 'center',
            minWidth: 100,

            renderHeader: () => (
                <Box position={'relative'} width={'100%'} height={'100%'} padding={0} mb={2} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                    <Icon name='partial-clock'></Icon>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }} >{t('intervals:time')}</Typography>
                    <Typography variant='caption'>({t('intervals:min')})</Typography>
                </Box>
            ), ...(printable ? {width:100}:{flex:1}),
            sortable: false,
            renderCell: (val: GridRenderCellParams<Sectional | CompiledInterval>) => {

                return (
                    <Box padding={0} display={'flex'} gap={1} alignItems={'center'} justifyContent={'center'} position={'relative'} flex={1} height={'100%'}>
                        <Typography variant='inherit'>
                            {get(val, 'row.parentId', null) ? (val.row as CompiledInterval)?.time : (val.row as Sectional)?.averages?.time}
                        </Typography>
                        {
                            renderFinishLineSection(val.row as any)
                        }
                    </Box>
                )
            }

        },
        {
            field: 'pace',
            headerAlign: 'center',
            minWidth: 100,
            renderHeader: () => (
                <Box position={'relative'} width={'100%'} height={'100%'} padding={0} mb={2} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                    <Icon name='speedometer'></Icon>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }} >{t('intervals:pace')}</Typography>
                    <Typography variant='caption'>({t(getSpeedUnit()?.key ?? '')})</Typography>

                </Box>
            ),
            ...(printable ? {width:100}:{flex:1}),
            sortable: false,
            renderCell: (val: GridRenderCellParams<Sectional>) => {
                if (val.row.gait == 0) {
                    return null; // do not display distance when inactivity
                }
                return (
                    <Box padding={0} display={'flex'} gap={1} alignItems={'center'} justifyContent={'center'} flexDirection={'column'} position={'relative'} flex={1} height={'100%'}>
                        <Typography variant='inherit'>
                            {get(val, 'row.parentId', null) ? getSpeedForChart(get(val, 'row.speed', 0), getSpeedUnit()?.value ?? "") : getSpeedForChart(get(val, 'row.averages.meanSpeed', 0), getSpeedUnit()?.value ?? "")}
                        </Typography>
                        {
                            renderFinishLineSection(val.row)
                        }
                    </Box>
                )
            }

        },

        {
            field: 'mean_heart_rate',
            headerAlign: 'center',
            minWidth: 100,
            renderHeader: () => (
                <Box position={'relative'} width={'100%'} height={'100%'} padding={0} mb={2} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                    <Icon name='heartbeat'></Icon>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }} >{t('intervals:averageFC')}</Typography>
                    <Typography variant='caption'>({t('intervals:bpm')})</Typography>
                </Box>
            ),
            ...(printable ? {width:100}:{flex:1}),
            sortable: false,

            renderCell: (val: GridRenderCellParams<Sectional>) => {
                return (
                    <Box padding={0} display={'flex'} gap={1} alignItems={'center'} justifyContent={'center'} position={'relative'} flex={1} height={'100%'}>
                        <Typography variant='inherit'>
                            {get(val, 'row.parentId', null) ? get(val, 'row.meanHR', '-') : get(val, 'row.averages.meanHeartRate', '-')}
                        </Typography>

                        {
                            renderFinishLineSection(val.row)
                        }
                    </Box>
                )
            }

        },
        {
            field: 'mean_tilt',
            headerAlign: 'center',
            sortable: false,
            minWidth: 100,
            renderHeader: () => (
                <Box position={'relative'} width={'100%'} height={'100%'} padding={0} mb={2} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                    <Icon name='weird-target'></Icon>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }} >{t('intervals:averageInclin')}</Typography>
                    <Typography variant='caption'>%</Typography>
                </Box>
            ),
            ...(printable ? {width:100}:{flex:1}),
            renderCell: (val: GridRenderCellParams<Sectional>) => {
                if (val.row.gait == 0) {
                    return null; // do not display distance when inactivity
                }

                let childTilt = get(val, 'row.tilt', 0) === 0 ? '0' : get(val, 'row.tilt', 0).toFixed(2);
                let parentTilt = get(val, 'row.averages.meanTilt', 0) === 0 ? '0' : get(val, 'row.averages.meanTilt', 0).toFixed(2);


                return (
                    <Box padding={0} display={'flex'} gap={1} alignItems={'center'} justifyContent={'center'} position={'relative'} flex={1} height={'100%'}>
                        <Typography variant='inherit'>
                            {get(val, 'row.parentId', null) ? childTilt : parentTilt}
                        </Typography>
                        {
                            renderFinishLineSection(val.row)
                        }
                    </Box>
                )
            }
        },
        {
            field: 'mean_stride_length',
            headerAlign: 'center',
            minWidth: 100,
            renderHeader: () => (
                <Box position={'relative'} width={'100%'} height={'100%'} padding={0} mb={2} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                    <Icon name='horseshoe-refresh'></Icon>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }} >{t('intervals:averageAmpl')}</Typography>
                    <Typography variant='caption'>({getMetric()})</Typography>
                </Box>
            ),
            ...(printable ? {width:100}:{flex:1}),
            sortable: false,
            renderCell: (val: GridRenderCellParams<Sectional>) => {

                return (
                    <Box padding={0} display={'flex'} gap={1} alignItems={'center'} justifyContent={'center'} position={'relative'} flex={1} height={'100%'}>
                        <Typography variant='inherit'>
                            {get(val, 'row.parentId', null) ? convertStrideLength(unitSystem, get(val, 'row.amp', 0)) : convertStrideLength(unitSystem, get(val, 'row.averages.meanStrideLength', 0))}
                        </Typography>

                        {
                            renderFinishLineSection(val.row)
                        }
                    </Box>
                )
            }
        },
        {
            field: 'mean_stride_frequency',
            headerAlign: 'center',
            sortable: false,
            minWidth: 100,
            renderHeader: () => (
                <Box position={'relative'} width={'100%'} height={'100%'} padding={0} mb={2} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                    <Icon name='horseshoe'></Icon>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }} >{t('intervals:averageCad')}</Typography>
                    <Typography variant='caption'>({t('intervals:stepPerSecond')})</Typography>
                </Box>
            ),
            ...(printable ? {width:100}:{flex:1}),
            renderCell: (val: GridRenderCellParams<Sectional>) => {

                return (
                    <Box padding={0} display={'flex'} gap={1} alignItems={'center'} justifyContent={'center'} position={'relative'} flex={1} height={'100%'}>
                        <Typography variant='inherit'>
                            {convertStrideFrequency(get(val, 'row.parentId', null) ? get(val, 'row.pace', 0) : get(val, 'row.averages.meanStrideFrequency', 0))}
                        </Typography>

                        {
                            renderFinishLineSection(val.row)
                        }
                    </Box>
                )
            }

        },

    ];


    return (

        <I18nDataGrid
            paginationModel={
                { pageSize: 100, page: 0 }
            }
            columns={columns}
            // autoHeight={printable}
            columnHeaderHeight={100}
            sx={{
                // maxHeight: '800px',
                // overflowY: 'auto',
                '& .MuiDataGrid-cell': {
                    padding: '0px',
                },
                '& .MuiDataGrid-columnHeaders': {
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                },

            }}

            disableColumnMenu
            hideFooterPagination={true}

            rows={uniqBy(Intervals, 'idSection')}
            getRowId={item => item?.idSection || uuidv4()}
            loading={loading}
            onRowClick={params => {
                if (params.row.gait == 0) return;

                return handleRowClick(params.row.idSection);
            }}
            disableRowSelectionOnClick
            
            
            getRowClassName={(param: GridRowClassNameParams<Sectional>) => {
                if (get(param, 'row.parentId', null)) {
                    return (time?.[currentIndex] >= get(param, 'row.startFloatTimestamp', 0) && time?.[currentIndex] <= get(param, 'row.endFloatTimestamp', 0)) ? (printable ? '' : 'row-active') : 'child_row'
                } else {
                    return (currentIndex >= param.row?.indexStart && currentIndex <= param.row?.indexEnd) ? (printable ? '' : 'row-active') : 'interval_row'
                }
            }}
        />
    )
}

export default IntervalsGrid